import React, {PropsWithChildren, useMemo} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {
	Avatar,
	Box,
	Grid,
	IconButton,
	Step,
	StepButton,
	StepConnector,
	Stepper,
	SvgIcon,
	Typography, useTheme
} from '@mui/material';
import {TemporalFormat} from "@variocube/app-ui";
import {useLocalization} from "../../i18n";
import {useOrder} from "../../context/order";
import {DisplayCurrency, notBlank, notNull} from "../../utils";
import {B2Site} from "../../data/client.gen";
import {DisplayUnitsCount} from "../../booking-utils";
import {applyDiscountReduction} from "../../order-utils";
import {useTenant} from "../../context/tenant";

import Locator from '../../assets/Glyph_052_Locator.svg';
import Calendar from '../../assets/Glyph_080_Calendar.svg';
import Locker from '../../assets/Glyph_082_Locker.svg';
import CreditCard from '../../assets/Glyph_081_Creditcard.svg';
import BackIcon from '../../assets/long-arrow-alt-left.svg';
import {parseZoneDateTimeFromDate, parseZoneDateTimeFromInstant} from "../../dates-utils";

interface OrderProgressProps {
	site: B2Site;
}

export function OrderProgress({site}: OrderProgressProps) {
	const navigate = useNavigate();
	const theme = useTheme();
	const {t} = useLocalization();
	const {step} = useParams<{ step: string }>();
	const {tenant} = useTenant();

	const stepLabel = useMemo(() => {
		switch (step) {
			case 'dates':
				return t('orders.steps.dates');
			case 'boxes':
				return t('orders.steps.lockers');
			case 'checkout':
				return t('orders.steps.basket');
			default:
				return site.name;
		}
	}, [step, site])

	function handleBackStep() {
		switch (step) {
			case 'boxes':
				navigate(`/${tenant?.id}/${site.id}/dates`);
				break;
			case 'checkout':
				navigate(`/${tenant?.id}/${site.id}/boxes`);
				break;
			case 'dates':
			default:
				navigate(`/${tenant?.id}`);
				break;
		}
	}

	return (
		<Box
			bgcolor="primary.main"
			sx={{
				p: 4
			}}
		>
			<Box
				display={{xs: 'none', sm: 'block'}}
				sx={{ width: '100%' }}
			>
				<Stepper
					alternativeLabel
					connector={(
						<StepConnector
							sx={{
								borderColor: theme.palette.primary.contrastText,
								marginTop: '6px',
								marginLeft: theme.spacing(2),
								marginRight: theme.spacing(2),
								'& > .MuiStepConnector-line': {
									borderColor: theme.palette.primary.contrastText,
								}
							}}
						/>
					)}
				>
					<Step>
						<StepSite
							onClick={() => navigate(`/${tenant?.id}`)}
							site={site}
						/>
					</Step>

					<Step>
						<StepDates
							active={step === 'dates'}
							disabled={false}
							onClick={() => {}}
							site={site}
						/>
					</Step>

					<Step>
						<StepBoxes
							active={step === 'boxes'}
							disabled={false}
							onClick={() => {}}
							site={site}
						/>
					</Step>

					<Step>
						<StepBasket
							active={step === 'checkout'}
							disabled={false}
							onClick={() => {}}
							site={site}
						/>
					</Step>
				</Stepper>
			</Box>
			<Box
				display={{xs: 'flex', sm: 'none'}}
				minHeight="3em"
			>
				<Grid
					container
					alignItems="stretch"
					sx={{
						color: theme.palette.primary.contrastText,
					}}
				>
					{step !== undefined && (
						<Grid item>
							<Box
								display="flex"
								alignItems="center"
								justifyContent="flex-start"
								height="100%"
							>
								<IconButton
									onClick={handleBackStep}
									sx={{
										color: theme.palette.primary.contrastText,
									}}
								>
									<BackIcon style={{width: '0.875em', height: '1.5rem'}} />
								</IconButton>
							</Box>
						</Grid>
					)}
					<Grid
						item
						xs
					>
						<Box
							display="flex"
							alignItems="center"
							justifyContent="center"
							height="100%"
							marginLeft='calc(-1.3125rem - 24px)'
						>
							<Typography variant="h2">{stepLabel}</Typography>
						</Box>
					</Grid>
				</Grid>
			</Box>
		</Box>
	);
}

function StepIcon({active, children}: PropsWithChildren<{ active?: boolean }>) {
	return (
		<Avatar
			variant="circular"
			color={active ? "primary" : "inherit"}
			sx={theme => ({
				backgroundColor: active ? theme.palette.primary.main : 'inherit',
				color: active ? theme.palette.primary.contrastText : "inherit",
			})}
		>
			<SvgIcon
				sx={{
					width: '40px',
					height: '40px',
				}}
			>
				{children}
			</SvgIcon>
		</Avatar>
	)
}

interface StepItemProps {
	site: B2Site;
	onClick: () => void;
	active?: boolean;
	disabled?: boolean;
}

function StepSite({active, disabled, onClick, site}: StepItemProps) {
	return (
		<StepButton
			icon={<StepIcon active={active}><Locator /></StepIcon>}
			disabled={disabled}
			onClick={onClick}
		>
			<Typography variant="body1"><strong>{site.name}</strong></Typography>
			<Typography variant="caption">
				<span>{site.street}</span>
				<br/>
				<span>{site.city}, {site.country}</span>
			</Typography>
		</StepButton>
	)
}

function StepDates({active, disabled, onClick, site: s}: StepItemProps) {
	const {t} = useLocalization();
	const {order: { dates: { from, until, fromDateTime, hours, unitCounts = 0, datesValid } }} = useOrder();

	const {
		bookingUnit,
		zoneId = 'UTC'
	} = s;
	const hasDates = datesValid && (notBlank(from) && notBlank(until)) || (notBlank(fromDateTime) && notNull(hours));

	return (
		<StepButton
			icon={<StepIcon active={active}><Calendar /></StepIcon>}
			disabled={disabled}
			onClick={onClick}
		>
			<Typography variant="body1"><strong>{t('orders.steps.dates')}</strong></Typography>
			{hasDates && (
				<>
					<Typography variant="body2" fontStyle="italic"><DisplayUnitsCount unit={bookingUnit} count={unitCounts} /></Typography>
					{unitCounts === 1 && (
						<Typography variant="caption">
							{notBlank(from) && <TemporalFormat value={parseZoneDateTimeFromDate(from as string, zoneId).toPlainDate()} />}
							{notBlank(fromDateTime) && (
								<span>{t('dates.from.placeholder')} <TemporalFormat value={parseZoneDateTimeFromInstant(fromDateTime as string, zoneId)} /></span>
							)}
						</Typography>
					)}
					&nbsp;
					{unitCounts !== 1 && (
						<Typography variant="caption">
							{notBlank(from) && (
								<span>{t('dates.from.placeholder')} <TemporalFormat value={parseZoneDateTimeFromDate(from as string, zoneId).toPlainDate()} /></span>
							)}
							{" "}
							{notBlank(until) && (
								<span>{t('dates.until.placeholder')} <TemporalFormat value={parseZoneDateTimeFromDate(until as string, zoneId).toPlainDate()} /></span>
							)}
							{notBlank(fromDateTime) && (
								<span>{t('dates.from.placeholder')} <TemporalFormat value={parseZoneDateTimeFromInstant(fromDateTime as string, zoneId)} /></span>
							)}
						</Typography>
					)}
				</>
			)}
		</StepButton>
	)
}

function StepBoxes({active, disabled, onClick}: StepItemProps) {
	const {t} = useLocalization();
	const {order: { boxes }} = useOrder();
	return (

		<StepButton
			icon={<StepIcon active={active}><Locker /></StepIcon>}
			disabled={disabled}
			onClick={onClick}
		>
			<Typography variant="body1"><strong>{t('orders.steps.lockers')}</strong></Typography>
			{Object.entries(boxes.types ?? {})
				.filter(([, quantity]) => quantity > 0)
				.map(([type, quantity]) => (
					<Typography key={type} variant="body2">{quantity}x {type}</Typography>
				))
			}
		</StepButton>
	)
}

function StepBasket({active, disabled, onClick, site}: StepItemProps) {
	const {t} = useLocalization();
	const {order: { discount: { data } }, price} = useOrder();
	return (
		<StepButton
			icon={<StepIcon active={active}><CreditCard /></StepIcon>}
			disabled={disabled}
			onClick={onClick}
		>
			<Typography variant="body1"><strong>{t('orders.steps.basket')}</strong></Typography>
			{notNull(price) && (
				<Typography variant="body2">
					{site.currency ?? 'EUR'} <DisplayCurrency amount={applyDiscountReduction(data, price)} />
				</Typography>
			)}
		</StepButton>
	)
}
